import React from 'react';
import styles from './StatusBadge.module.css';

/**
 * Predefined badge color variants that map to CSS variables or hex values
 * These are the standard colors used across the application for status indicators
 */
export type BadgeColorVariant =
  | 'success'      // Green - successful, active, live
  | 'warning'      // Orange - pending, in-progress
  | 'error'        // Red - cancelled, failed
  | 'neutral'      // Grey - offline, refunded, inactive
  | 'info'         // Blue - informational
  | 'accent'       // Orange accent - ambassador, special status
  | 'custom';      // Use custom color prop

/**
 * Badge display variants
 */
export type BadgeVariant = 'filled' | 'outlined';

export interface StatusBadgeProps {
  /** The text to display in the badge */
  label: string;
  /** Predefined color variant or 'custom' to use backgroundColor prop */
  colorVariant?: BadgeColorVariant;
  /** Custom background color (only used when colorVariant is 'custom') */
  backgroundColor?: string;
  /** Custom text color (only used when colorVariant is 'custom') */
  textColor?: string;
  /** Badge display style - filled (solid background) or outlined (border only) */
  variant?: BadgeVariant;
  /** Whether to capitalize the label text */
  capitalize?: boolean;
  /** Additional CSS class name */
  className?: string;
  /** Additional inline styles */
  style?: React.CSSProperties;
  /** Test ID for testing purposes */
  testId?: string;
}

/**
 * Maps color variants to CSS variable names for filled badges
 * Note: We use #ffffff directly for text instead of --text-inverse because
 * --text-inverse changes in dark mode to a dark color, but badge text should always be white
 */
const colorVariantMap: Record<Exclude<BadgeColorVariant, 'custom'>, { bg: string; text: string }> = {
  success: {
    bg: 'var(--status-success, #56b58b)',
    text: '#ffffff'
  },
  warning: {
    bg: 'var(--status-warning, #e66708)',
    text: '#ffffff'
  },
  error: {
    bg: 'var(--status-error, #da3030)',
    text: '#ffffff'
  },
  neutral: {
    bg: 'var(--status-neutral, #afb0b4)',
    text: '#ffffff'
  },
  info: {
    bg: 'var(--status-info, #2185d0)',
    text: '#ffffff'
  },
  accent: {
    bg: 'var(--gr-light-orange, #f5a623)',
    text: '#ffffff'
  }
};

/**
 * A reusable badge component for displaying status with consistent styling.
 * 
 * @example
 * // Using predefined color variant
 * <StatusBadge label="Successful" colorVariant="success" />
 * 
 * @example
 * // Using outlined variant
 * <StatusBadge label="Applicant" colorVariant="accent" variant="outlined" />
 * 
 * @example
 * // Using custom colors
 * <StatusBadge 
 *   label="Custom Status" 
 *   colorVariant="custom" 
 *   backgroundColor="#purple" 
 *   textColor="#white" 
 * />
 */
export const StatusBadge: React.FC<StatusBadgeProps> = ({
  label,
  colorVariant = 'neutral',
  backgroundColor,
  textColor,
  variant = 'filled',
  capitalize = true,
  className = '',
  style = {},
  testId
}) => {
  // Determine colors based on variant and colorVariant
  let bgColor: string;
  let color: string;
  let borderColor: string | undefined;

  if (colorVariant === 'custom') {
    bgColor = variant === 'filled' ? (backgroundColor || '#afb0b4') : 'transparent';
    color = textColor || (variant === 'filled' ? '#ffffff' : backgroundColor || '#afb0b4');
    borderColor = variant === 'outlined' ? (backgroundColor || '#afb0b4') : undefined;
  } else {
    const colors = colorVariantMap[colorVariant];
    bgColor = variant === 'filled' ? colors.bg : 'transparent';
    color = variant === 'filled' ? colors.text : colors.bg;
    borderColor = variant === 'outlined' ? colors.bg : undefined;
  }

  const badgeStyle: React.CSSProperties = {
    backgroundColor: bgColor,
    color,
    border: borderColor ? `1px solid ${borderColor}` : undefined,
    ...style
  };

  const classNames = [
    styles.statusBadge,
    capitalize ? styles.capitalize : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <span
      className={classNames}
      style={badgeStyle}
      data-testid={testId}
    >
      {label}
    </span>
  );
};

export default StatusBadge;
